import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listService } from '@/services/listService';
import { AddCandidatesRequest } from '@/types/list';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';

export const useListDetail = (listId: string) => {
  return useQuery({
    queryKey: ['list', listId],
    queryFn: () => listService.getList(listId),
    enabled: !!listId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useListMembers = (listId: string) => {
  return useQuery({
    queryKey: ['list-members', listId],
    queryFn: () => listService.getListMembers(listId),
    enabled: !!listId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useAddCandidatesToList = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (request: AddCandidatesRequest) => listService.addCandidatesToList(request),
    onSuccess: (_, { list_id }) => {
      queryClient.invalidateQueries({ queryKey: ['list-members', list_id] });
      queryClient.invalidateQueries({ queryKey: ['list', list_id] });
      queryClient.invalidateQueries({ queryKey: ['lists'] });
      toast({
        title: "Success",
        description: "Candidates added to list successfully",
      });
    },
    onError: (error) => {
      console.error('Error adding candidates to list:', error);
      toast({
        title: "Error",
        description: "Failed to add candidates to list. Please try again.",
        variant: "destructive",
      });
    },
  });
};

export const useRemoveCandidateFromList = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ listId, candidateId }: { listId: string; candidateId: string }) =>
      listService.removeCandidateFromList(listId, candidateId),
    onSuccess: (_, { listId }) => {
      queryClient.invalidateQueries({ queryKey: ['list-members', listId] });
      queryClient.invalidateQueries({ queryKey: ['list', listId] });
      queryClient.invalidateQueries({ queryKey: ['lists'] });
      toast({
        title: "Success",
        description: "Candidate removed from list successfully",
      });
    },
    onError: (error) => {
      console.error('Error removing candidate from list:', error);
      toast({
        title: "Error",
        description: "Failed to remove candidate from list. Please try again.",
        variant: "destructive",
      });
    },
  });
};

export const useSearchCandidatesByAI = () => {
  const { toast } = useToast();

  return useMutation({
    mutationFn: listService.searchCandidatesByAI,
    onError: (error) => {
      console.error('Error searching candidates by AI:', error);
      toast({
        title: "Error",
        description: "Failed to search candidates. Please try again.",
        variant: "destructive",
      });
    },
  });
};

export const useGetCandidatesNotInList = (listId: string) => {
  return useQuery({
    queryKey: ['candidates-not-in-list', listId],
    queryFn: () => listService.getCandidatesNotInList(listId),
    enabled: !!listId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useUpdateList = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: any }) => listService.updateList(id, updates),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['list', id] });
      queryClient.invalidateQueries({ queryKey: ['lists'] });
      toast({
        title: "Success",
        description: "List updated successfully",
      });
    },
    onError: (error) => {
      console.error('Error updating list:', error);
      toast({
        title: "Error",
        description: "Failed to update list. Please try again.",
        variant: "destructive",
      });
    },
  });
};

export const useDeleteList = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (id: string) => listService.deleteList(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lists'] });
      toast({
        title: "Success",
        description: "List deleted successfully",
      });
      navigate('/lists');
    },
    onError: (error) => {
      console.error('Error deleting list:', error);
      toast({
        title: "Error",
        description: "Failed to delete list. Please try again.",
        variant: "destructive",
      });
    },
  });
};
